import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import * as authApi from '@/lib/auth'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [screenLocked, setScreenLocked] = useState(false)
  const [twoFactorPending, setTwoFactorPending] = useState(false)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    try {
      const nextUser = await authApi.fetchMe()
      setUser(nextUser)
      setScreenLocked(Boolean(nextUser.screen_locked))
      return nextUser
    } catch {
      setUser(null)
      setScreenLocked(false)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  const lock = useCallback(async () => {
    if (!user) return
    try {
      await authApi.lockScreen()
    } catch {
      /* still show lock UI locally if API fails */
    }
    setScreenLocked(true)
  }, [user])

  const unlock = useCallback(async (password) => {
    const nextUser = await authApi.unlockScreen(password)
    setUser(nextUser)
    setScreenLocked(false)
    return nextUser
  }, [])

  const login = useCallback(async (email, password, remember = false) => {
    const result = await authApi.login(email, password, remember)
    if ('two_factor_required' in result && result.two_factor_required) {
      setTwoFactorPending(true)
      return { twoFactorRequired: true }
    }
    setUser(result)
    setTwoFactorPending(false)
    setScreenLocked(false)
    return { twoFactorRequired: false, user: result }
  }, [])

  const completeTwoFactorChallenge = useCallback(async ({ code, recoveryCode }) => {
    const nextUser = await authApi.completeTwoFactorChallenge({ code, recoveryCode })
    setUser(nextUser)
    setTwoFactorPending(false)
    setScreenLocked(false)
    return nextUser
  }, [])

  const cancelTwoFactor = useCallback(() => {
    setTwoFactorPending(false)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      /* clear local state even if request fails */
    }
    setUser(null)
    setScreenLocked(false)
    setTwoFactorPending(false)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      screenLocked,
      twoFactorPending,
      isAuthenticated: Boolean(user),
      refreshUser,
      login,
      completeTwoFactorChallenge,
      cancelTwoFactor,
      lock,
      unlock,
      logout,
    }),
    [
      user,
      loading,
      screenLocked,
      twoFactorPending,
      refreshUser,
      login,
      completeTwoFactorChallenge,
      cancelTwoFactor,
      lock,
      unlock,
      logout,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** @returns {NonNullable<import('react').ContextType<typeof AuthContext>>} */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
